import { DeductionDetailJson } from "@/api"
import { DeductionAction } from "@/api/deduction_action"
import { DisputeType } from "@/api/dispute"
import { getDisputeAction } from "@/deduction_table/actions/dispute_utils.ts"
import { exactCurrencyFormatter } from "@/utils"

export type ButtonAction =
  | { type: "wizard"; label: string }
  | { type: "big_wiz"; label: string }
  | { type: "assign"; label: string }
  | { type: "request_backup"; label: string }
  | { type: "file_dispute"; label: string; url?: string }
  | { type: "dispute_short_pay"; label: string }
  | { type: "view_deduction"; label: string; deductionId: string }
  | { type: "assign_repayment"; label: string }
  | { type: "change_repayment"; label: string }
  | { type: "remove_duplicate"; label: string }
  | { type: "request_exit_pass"; label: string }

export type StatusIndicator =
  | { type: "disputed"; label: string; clickable: boolean }
  | { type: "error"; message: string; clickable: boolean }

export interface SecondaryActionsModel {
  status?: StatusIndicator
  buttons: ButtonAction[] // first = primary, rest = dropdown
}

// Returns the set of button types that the next action already covers,
// so we don't duplicate them in the detail buttons.
function nextActionCovers(nextAction: DeductionAction | null | undefined): Set<ButtonAction["type"]> {
  let covered = new Set<ButtonAction["type"]>()
  if (!nextAction) return covered
  switch (nextAction.type) {
    case "file_dispute":
      covered.add("file_dispute")
      break
    case "assign_task":
      covered.add("assign")
      break
    case "match_original":
      covered.add("view_deduction")
      covered.add("assign_repayment")
      covered.add("change_repayment")
      break
    case "request_backup":
      covered.add("request_backup")
      break
    case "ask_the_wizard":
      covered.add("wizard")
      break
  }
  return covered
}

// UNFI SAS post-audit recovery deductions (SASIF, SASW, SAS...) use the auto-dispute flow.
function isSasRecoveryDeduction(deduction: DeductionDetailJson): boolean {
  return deduction.source === "unfi" && deduction.invoice_number.toUpperCase().startsWith("SAS")
}

/**
 * Where the "Request exit pass" button goes. The exit pass (a co-packer warehouse's gate pass) is
 * the proof C&S short-ship claims are disputed with, and only C&S asks for it: a C&S short ship
 * gets it as the primary button, any other C&S deduction keeps it in the dropdown, and no other
 * customer's deduction shows it at all (Sugo, 10/28 call).
 */
export type ExitPassPlacement = "primary" | "menu" | null

export function exit_pass_placement(
  deduction: Pick<DeductionDetailJson, "source" | "kind"> & { category?: string | null; category_description?: string | null }
): ExitPassPlacement {
  if (deduction.source !== "cands" || deduction.kind !== "deduction") return null
  const text = `${deduction.category ?? ""} ${deduction.category_description ?? ""}`
  return /short|shortage|qty|quantity/i.test(text) ? "primary" : "menu"
}

/** Extra context the button set depends on but the deduction itself doesn't carry. */
export interface SecondaryActionsOptions {
  /** Whether the current signed-in user may trigger BigWiz. */
  canRunBigWiz?: boolean
}

/** Whether the row already carries a short pay claim (the status line then reads "Disputed"). */
export function short_pay_already_disputed(deduction: Pick<DeductionDetailJson, "dispute">): boolean {
  return deduction.dispute?.type === DisputeType.ShortPay
}

export function getSecondaryActions(
  deduction: DeductionDetailJson,
  options: SecondaryActionsOptions = {}
): SecondaryActionsModel {
  let disputeAction = getDisputeAction(deduction, deduction.dispute)
  let covered = nextActionCovers(deduction.action)
  let model: SecondaryActionsModel = { buttons: [] }

  // Repayments have a simplified button set
  if (deduction.kind === "repayment") {
    if (disputeAction?.type === "repayment" && disputeAction.id) {
      // Matched repayment: only show re-assign button
      if (!covered.has("change_repayment")) {
        model.buttons.push({ type: "change_repayment", label: "Re-assign Repayment" })
      }
    }
    // Unmatched repayments have no secondary buttons (next action handles it)
    return model
  }

  // If no backup, request backup is the primary action — unless the source documents that it never
  // publishes any, in which case the server says so and we explain rather than offer a dead button.
  // (This replaces a hardcoded UNFI CV/SSA regex; that rule now lives in db::deduction::backup_policy
  // alongside Tony's, so the two can't drift apart.)
  let skipBackup = !!deduction.backup_unavailable_reason
  let pendingBackup = deduction.dispute?.type === DisputeType.Backup
  if (!deduction.has_backup && !covered.has("request_backup") && !skipBackup && deduction.kind !== "invoice" && !pendingBackup) {
    model.buttons.push({ type: "request_backup", label: "Request Backup" })
  }

  // Add wizard as the primary action (unless covered by next action). SAS recovery uses the
  // auto-dispute flow; the wizard is not useful and would hide File Dispute as the primary control.
  if (!covered.has("wizard") && deduction.kind !== "invoice" && !isSasRecoveryDeduction(deduction)) {
    model.buttons.push({ type: "wizard", label: "Ask the Wizard" })
  }

  if (disputeAction) {
    if (disputeAction.type === "active") {
      let disputedAmount = deduction.dispute?.amount ?? deduction.dispute_amount
      let amount = disputedAmount ? exactCurrencyFormatter(disputedAmount) : null
      let label = amount ? `Disputed (${amount})` : "Disputed"
      model.status = { type: "disputed", label, clickable: false }
      if ((deduction.kind === "deduction" || deduction.kind === "invoice") && !covered.has("file_dispute")) {
        model.buttons.push({ type: "file_dispute", label: "File New Dispute" })
      }
    } else if (disputeAction.type === "error") {
      model.status = { type: "error", message: disputeAction.message, clickable: true }
    } else if (disputeAction.type === "can_file" && !covered.has("file_dispute")) {
      model.buttons.push({
        type: "file_dispute",
        label: disputeAction.cta,
        url: disputeAction.url ?? undefined,
      })
    }
  }

  // An invoice payment row can be short paid — the customer remitted less than the invoice with no
  // chargeback explaining the gap. That is asked back from AP through its own channel, separate
  // from the early-pay dispute the drawer files, and it does not need the accounting-backed short
  // pays screen. Offered for every invoice row; the server says when it cannot go (non-UNFI,
  // missing config, already filed).
  if (deduction.kind === "invoice" && !short_pay_already_disputed(deduction)) {
    model.buttons.push({ type: "dispute_short_pay", label: "Dispute short pay" })
  }

  if (!covered.has("assign")) {
    model.buttons.push({ type: "assign", label: "Assign task" })
  }

  // C&S only: the exit pass request. Primary on a short ship, otherwise in the dropdown.
  const exit_pass = exit_pass_placement(deduction)
  if (exit_pass === "primary") {
    model.buttons.unshift({ type: "request_exit_pass", label: "Request exit pass" })
  } else if (exit_pass === "menu") {
    model.buttons.push({ type: "request_exit_pass", label: "Request exit pass" })
  }

  // A row booked twice (e.g. a check line and its ledger twin) can be merged into its other copy
  // from here; the server refuses anything that is not plainly the same charge. Never the primary
  // button: it is a cleanup, not the suggested next step.
  if (model.buttons.length > 0) {
    model.buttons.push({ type: "remove_duplicate", label: "Remove duplicate" })
  }

  // BigWiz always goes last so it lands in the dropdown rather than becoming the
  // primary button — it is a deliberate re-analysis, not the suggested next step.
  if (options.canRunBigWiz !== false) {
    model.buttons.push({ type: "big_wiz", label: "Run BigWiz" })
  }

  return model
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function makeDeduction(overrides: Partial<DeductionDetailJson> = {}): DeductionDetailJson {
    return {
      id: "test-id",
      org_id: "org-1",
      source: "unfi",
      kind: "deduction",
      invoice_amount: -100,
      invoice_date: "2025-01-01",
      invoice_number: "INV-001",
      has_backup: true,
      has_triage_result: false,
      action: null,
      dispute: null,
      dispute_amount: null,
      check_date: new Date().toISOString().split("T")[0],
      ...overrides,
    } as any
  }

  describe("exit_pass_placement", () => {
    it("is the primary button on a C&S short ship", () => {
      // Sugo's C&S rows: "Operational Issues / Qty Short DEDUCTION 66 …" and "Invoice Discrepancy / Short ship".
      expect(exit_pass_placement({ source: "cands", kind: "deduction", category: "Operational Issues", category_description: "Qty Short DEDUCTION 66  RINALDI SCE TOM & BSL" })).toBe("primary")
      expect(exit_pass_placement({ source: "cands", kind: "deduction", category: "Invoice Discrepancy", category_description: "Short ship" })).toBe("primary")
      const model = getSecondaryActions(makeDeduction({ source: "cands", category: "Invoice Discrepancy", category_description: "Short ship" } as any))
      expect(model.buttons[0]).toEqual({ type: "request_exit_pass", label: "Request exit pass" })
    })

    it("sits in the dropdown on any other C&S deduction", () => {
      expect(exit_pass_placement({ source: "cands", kind: "deduction", category: "Retailer Promotion", category_description: "Scan" })).toBe("menu")
      const model = getSecondaryActions(makeDeduction({ source: "cands", category: "Retailer Promotion", category_description: "Scan" } as any))
      const types = model.buttons.map(b => b.type)
      expect(types[0]).not.toBe("request_exit_pass")
      expect(types).toContain("request_exit_pass")
    })

    it("never appears for another customer, an invoice or a repayment", () => {
      expect(exit_pass_placement({ source: "unfi", kind: "deduction", category: "Invoice Discrepancy", category_description: "Short ship" })).toBeNull()
      expect(exit_pass_placement({ source: "cands", kind: "invoice", category: "", category_description: "" })).toBeNull()
      const model = getSecondaryActions(makeDeduction({ source: "kehe", category: "Invoice Discrepancy", category_description: "Short ship" } as any))
      expect(model.buttons.map(b => b.type)).not.toContain("request_exit_pass")
    })
  })

  describe("nextActionCovers", () => {
    // We test indirectly via getSecondaryActions since nextActionCovers is private

    it("hides wizard button when next action is ask_the_wizard", () => {
      let d = makeDeduction({ action: { type: "ask_the_wizard" } })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("wizard")
    })

    it("hides file_dispute button when next action is file_dispute", () => {
      let d = makeDeduction({
        action: { type: "file_dispute", suggested_message: null, dispute_type: DisputeType.Chargeback },
      })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("file_dispute")
    })

    it("shows wizard button when there is no next action", () => {
      let d = makeDeduction({ action: undefined })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).toContain("wizard")
    })
  })

  describe("getSecondaryActions", () => {
    it("wizard is the first button (primary) when deduction has backup", () => {
      let d = makeDeduction({ has_backup: true })
      let model = getSecondaryActions(d)
      expect(model.buttons[0]?.type).toBe("wizard")
    })

    it("UNFI SASIF deduction has File Dispute as primary (no wizard)", () => {
      let d = makeDeduction({
        has_backup: true,
        invoice_number: "SASIF20190411814PBA",
      })
      let model = getSecondaryActions(d)
      expect(model.buttons.map(b => b.type)).not.toContain("wizard")
      expect(model.buttons[0]?.type).toBe("file_dispute")
      expect(model.buttons[0]?.label).toBe("File Dispute")
    })

    it("UNFI SAS (non-SASIF) deduction has File Dispute as primary (no wizard)", () => {
      let d = makeDeduction({
        has_backup: true,
        invoice_number: "SAS25110714",
      })
      let model = getSecondaryActions(d)
      expect(model.buttons.map(b => b.type)).not.toContain("wizard")
      expect(model.buttons[0]?.type).toBe("file_dispute")
      expect(model.buttons[0]?.label).toBe("File Dispute")
    })

    it("request_backup is the first button when no backup", () => {
      let d = makeDeduction({ has_backup: false })
      let model = getSecondaryActions(d)
      expect(model.buttons[0]?.type).toBe("request_backup")
    })

    it("request_backup is hidden when there is an active backup dispute", () => {
      let d = makeDeduction({
        has_backup: false,
        dispute_id: "019d409e-96b0-57b0-9ca1-eca50973e956",
        dispute: {
          id: "019d409e-96b0-57b0-9ca1-eca50973e956",
          org_id: "org-1",
          deduction_id: "test-id",
          created_at: "2026-03-30T21:20:28Z",
          updated_at: null,
          source: "unfi",
          user_id: "user-1",
          type: DisputeType.Backup,
          amount: 319.14,
          dispute_type_description: null,
          message: "Please provide backup",
          dispute_file_s3_uris: [],
          sent_at: "2026-03-30T22:20:30Z",
          thread_id: "thread-1",
        } as any,
      })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("request_backup")
    })

    it("wizard is not shown for kind=invoice", () => {
      let d = makeDeduction({ kind: "invoice", has_backup: true })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("wizard")
    })

    it("dispute_short_pay is offered on invoice rows only", () => {
      let invoice = makeDeduction({ kind: "invoice", has_backup: true })
      expect(getSecondaryActions(invoice).buttons.map(b => b.type)).toContain("dispute_short_pay")
      let deduction = makeDeduction({ kind: "deduction", has_backup: true })
      expect(getSecondaryActions(deduction).buttons.map(b => b.type)).not.toContain("dispute_short_pay")
      let repayment = makeDeduction({ kind: "repayment" })
      expect(getSecondaryActions(repayment).buttons.map(b => b.type)).not.toContain("dispute_short_pay")
    })

    it("dispute_short_pay is hidden once a short pay dispute is on the row", () => {
      let d = makeDeduction({
        kind: "invoice",
        has_backup: true,
        dispute_id: "019d409e-96b0-57b0-9ca1-eca50973e956",
        dispute: {
          id: "019d409e-96b0-57b0-9ca1-eca50973e956",
          org_id: "org",
          created_at: "2026-03-14T00:00:00Z",
          updated_at: "2026-03-14T00:00:00Z",
          source: "unfi",
          user_id: "u",
          type: DisputeType.ShortPay,
          amount: 1970.1,
          dispute_type_description: null,
          message: "",
          dispute_file_s3_uris: [],
          sent_at: null,
          thread_id: null,
        } as any,
      })
      expect(getSecondaryActions(d).buttons.map(b => b.type)).not.toContain("dispute_short_pay")
      expect(getSecondaryActions(d).status?.type).toBe("disputed")
    })

    it("request_backup is not shown for kind=invoice even without backup", () => {
      let d = makeDeduction({ has_backup: false, kind: "invoice" })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("request_backup")
    })

    it("offers Run BigWiz to brand users", () => {
      let d = makeDeduction({ has_backup: true })
      let model = getSecondaryActions(d)
      expect(model.buttons.map(b => b.type)).toContain("big_wiz")
    })

    it("offers Run BigWiz to admins, never as the primary button", () => {
      let d = makeDeduction({ has_backup: true })
      let model = getSecondaryActions(d, { canRunBigWiz: true })
      let types = model.buttons.map(b => b.type)
      expect(types).toContain("big_wiz")
      expect(types[0]).not.toBe("big_wiz")
      expect(types.indexOf("big_wiz")).toBe(types.length - 1)
    })

    it("file_dispute goes to dropdown (not primary) when it can be filed", () => {
      let d = makeDeduction({ has_backup: true })
      let model = getSecondaryActions(d)
      let fileDisputeIdx = model.buttons.findIndex(b => b.type === "file_dispute")
      if (fileDisputeIdx >= 0) {
        expect(fileDisputeIdx).toBeGreaterThan(0)
      }
    })
  })

  describe("repayment secondary actions", () => {
    it("unmatched repayment has no secondary buttons", () => {
      let d = makeDeduction({
        kind: "repayment",
        action: { type: "match_original" },
        repayment_for: undefined,
      })
      let model = getSecondaryActions(d)
      expect(model.buttons).toEqual([])
    })

    it("matched repayment shows only re-assign button", () => {
      let d = makeDeduction({
        kind: "repayment",
        action: undefined,
        repayment_for: "original-deduction-id",
      })
      let model = getSecondaryActions(d)
      expect(model.buttons).toHaveLength(1)
      expect(model.buttons[0]?.type).toBe("change_repayment")
      expect(model.buttons[0]?.label).toBe("Re-assign Repayment")
    })

    it("repayment never shows request_backup", () => {
      let d = makeDeduction({
        kind: "repayment",
        has_backup: false,
        repayment_for: undefined,
        action: { type: "match_original" },
      })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("request_backup")
    })

    it("repayment never shows wizard", () => {
      let d = makeDeduction({
        kind: "repayment",
        repayment_for: "original-deduction-id",
      })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("wizard")
    })

    it("repayment never shows assign task", () => {
      let d = makeDeduction({
        kind: "repayment",
        repayment_for: "original-deduction-id",
      })
      let model = getSecondaryActions(d)
      let types = model.buttons.map(b => b.type)
      expect(types).not.toContain("assign")
    })

    it("matched repayment with match_original next action hides change_repayment", () => {
      // Edge case: if backend returns match_original but repayment_for is set
      let d = makeDeduction({
        kind: "repayment",
        action: { type: "match_original" },
        repayment_for: "original-deduction-id",
      })
      let model = getSecondaryActions(d)
      expect(model.buttons).toEqual([])
    })
  })
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  // Minimal stand-in: getSecondaryActions only reads these fields for the backup decision.
  function deduction(overrides: Partial<DeductionDetailJson> = {}): DeductionDetailJson {
    return {
      id: "d1",
      source: "tonys",
      invoice_number: "629868",
      kind: "deduction",
      has_backup: false,
      action: null,
      dispute: null,
      ...overrides,
    } as unknown as DeductionDetailJson
  }

  describe("request backup suppression", () => {
    it("offers Request Backup when backup is expected", () => {
      let model = getSecondaryActions(deduction({ source: "kehe", backup_unavailable_reason: null }))
      expect(model.buttons.some(b => b.type === "request_backup")).toBe(true)
    })

    it("suppresses Request Backup when the source publishes none", () => {
      // Tony's documents six fee types whose original request *is* the backup; offering the button
      // sends someone chasing a document that will never exist.
      let model = getSecondaryActions(
        deduction({ backup_unavailable_reason: "Tony's does not publish backup for ... fees" }),
      )
      expect(model.buttons.some(b => b.type === "request_backup")).toBe(false)
    })

    it("still suppresses once backup has actually arrived", () => {
      let model = getSecondaryActions(deduction({ source: "kehe", has_backup: true, backup_unavailable_reason: null }))
      expect(model.buttons.some(b => b.type === "request_backup")).toBe(false)
    })
  })
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function row(overrides: Partial<DeductionDetailJson> = {}): DeductionDetailJson {
    return {
      id: "d1",
      source: "svharbor",
      invoice_number: "18157104",
      kind: "deduction",
      has_backup: true,
      action: null,
      dispute: null,
      ...overrides,
    } as unknown as DeductionDetailJson
  }

  describe("remove duplicate", () => {
    it("is offered in the dropdown, never as the primary button", () => {
      let types = getSecondaryActions(row()).buttons.map(b => b.type)
      expect(types).toContain("remove_duplicate")
      expect(types[0]).not.toBe("remove_duplicate")
    })

    it("is not offered on repayments", () => {
      let types = getSecondaryActions(row({ kind: "repayment" })).buttons.map(b => b.type)
      expect(types).not.toContain("remove_duplicate")
    })
  })
}
